<template>
  <v-container fluid class="ma-page">
    <v-card class="ma-card" flat>
      <!-- Header -->
      <div class="d-flex justify-space-between align-start flex-wrap" style="gap: 12px">
        <div>
          <div class="ma-title">My Activity</div>
          <div class="ma-subtitle">Track and manage your daily activities, client work, and billable hours.</div>
        </div>
        <v-btn v-if="authorize_add" to="/new-activity" depressed height="40" class="ma-btn-primary">
          <v-icon left>mdi-plus</v-icon> New Activity
        </v-btn>
      </div>

      <!-- Filter bar -->
      <div class="ma-filter mt-5">
        <div class="ma-filter-row">
          <v-text-field v-model="search" prepend-inner-icon="mdi-magnify"
            placeholder="Search assignment, client, or details..." outlined dense hide-details clearable
            class="ma-input ma-search" @keyup.enter="findAllByAPISearch"
            @click:clear="$nextTick(() => { search = ''; findAllByAPISearch(); })"></v-text-field>

          <!-- Date Range -->
          <v-menu v-model="menu" :close-on-content-click="false" transition="scale-transition" offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field :value="dateRangeText" placeholder="Date Range" prepend-inner-icon="mdi-calendar-blank-outline"
                append-icon="mdi-chevron-down" readonly v-bind="attrs" v-on="on" outlined dense hide-details
                class="ma-input ma-date"></v-text-field>
            </template>
            <v-date-picker v-model="dateRange" range @change="onDateRangeChange"></v-date-picker>
          </v-menu>

          <!-- Assignment / Case (filter เดิม) -->
          <v-autocomplete v-model="searchChargeJob" :items="jobTitle_list" placeholder="Assignment / Case"
            item-text="name" item-value="name" outlined dense hide-details clearable class="ma-input ma-select"
            @change="findAllByAPISearch"
            @click:clear="$nextTick(() => { searchChargeJob = null; })"></v-autocomplete>

          <!-- Charge Type -->
          <v-autocomplete v-model="searchCharge" :items="charge_list" placeholder="Charge Type" item-text="name"
            item-value="value" outlined dense hide-details clearable class="ma-input ma-select"
            @change="findAllByAPISearch"
            @click:clear="$nextTick(() => { searchCharge = null; })"></v-autocomplete>

          <div class="ma-clear">
            <a @click="clearAll">Clear All</a>
          </div>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="d-flex justify-space-between align-center flex-wrap mt-4 mb-3" style="gap: 12px">
        <div class="d-flex align-center" style="gap: 10px">
          <v-select v-model="itemsPerPage" @change="onSearchPagination" :items="item_per_page_list" dense outlined
            hide-details class="ma-input" style="max-width: 90px"></v-select>
          <span class="ma-muted-text">items per page</span>
        </div>
        <div class="d-flex align-center" style="gap: 12px">
          <span class="ma-muted-text">{{ showingText }}</span>
          <v-btn icon small outlined class="ma-nav" :disabled="page <= 1 || tableLoading" @click="prevPage">
            <v-icon small>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn icon small outlined class="ma-nav" :disabled="page >= pageCount || tableLoading" @click="nextPage">
            <v-icon small>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Table -->
      <div class="ma-table-wrap">
        <v-data-table :headers="headers" :items="mDataArray2" :server-items-length="totalItems"
          :options.sync="options" @update:options="onOptionsUpdate" hide-default-footer class="ma-table"
          :loading="tableLoading" loading-text="Loading... Please wait">
          <!-- Custom loading slot สำหรับแสดง loading แบบวงกลม -->
          <template v-slot:progress>
            <div class="text-center pa-6">
              <v-progress-circular indeterminate color="#1f2d4d" size="40" width="4"></v-progress-circular>
              <div class="mt-3 ma-muted-text">กำลังโหลดข้อมูล...</div>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="ma-empty">
              <div class="ma-empty-icon">
                <v-icon color="#6b7280">mdi-clipboard-text-outline</v-icon>
              </div>
              <div class="ma-empty-title">No activity found</div>
              <div class="ma-empty-sub">Try adjusting your search or filter, or create a new activity.</div>
              <v-btn v-if="authorize_add" to="/new-activity" outlined height="38" class="ma-btn-outline mt-4">
                <v-icon left small>mdi-plus</v-icon> New Activity
              </v-btn>
            </div>
          </template>

          <template v-slot:[`item.checkindate_show`]="{ item }">
            {{ datePart(item.checkindate_show) }}
          </template>
          <template v-slot:[`item.start_end`]="{ item }">
            {{ timePart(item.checkindate_show) }} - {{ timePart(item.checkoutdate_show) }}
          </template>
          <template v-slot:[`item.chargetype`]="{ item }">
            <span v-if="item.chargetype" class="ma-chip">{{ item.chargetype }}</span>
            <span v-else class="ma-muted-text">-</span>
          </template>
          <template v-slot:[`item.detail`]="{ item }">
            <div class="ma-detail" :title="item.detail">{{ item.detail || "-" }}</div>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn v-if="authorize_edit" icon small class="ma-act ma-act--edit" @click="openeditItem(item.id)">
              <v-icon small>mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn v-if="authorize_del" icon small class="ma-act ma-act--del ml-2" @click="deleteItem(item.id)">
              <v-icon small>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </div>

      <v-dialog v-model="dialogDeleteActivity" max-width="500px">
        <v-card style="padding-top: 30px; padding-bottom: 30px;">
          <v-card-title style="padding-bottom: 20px;">
            <v-row justify="center">
              <span class="red--text text-h5 ju">Are you sure?</span>
            </v-row>
          </v-card-title>
          <br />
          <v-card-subtitle>
            <v-row justify="center">
              <span class="black--text">Are you sure you want to delete this item</span>
            </v-row>
          </v-card-subtitle>
          <v-card-actions style="padding-top: 20px">
            <v-spacer></v-spacer>
            <v-btn depressed width="100" class="mr-3 buttonblue" color="red" @click="deleteItemConfirm">
              Delete
            </v-btn>
            <v-btn depressed outlined width="100" @click="closeDelete">
              Cancel
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="dialogDelete" max-width="500px">
        <v-card>
          <v-card-title class="text-h5">Are you sure you want to delete this item?</v-card-title>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="closeDelete">Cancel</v-btn>
            <v-btn color="blue darken-1" text @click="deleteItemConfirm">OK</v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <SuccessDialog :status="dialogAdd" :text_color="text_color" :title="title" :message="message" />
    </v-card>
  </v-container>
</template>

<script>
import SuccessDialog from "@/components/cards/SuccessDialog";
import api from "@/services/api";
import { server } from "@/services/constants";
import { language } from "../language/Advancelist.js";
import { tolocalestringnumber } from "../computing/tolocalestringnumber";
import unity from "@/unity/unity";

export default {
  name: "advance-list",
  data(vm) {
    return {
      authorize_view: true,
      authorize_add: true,
      authorize_edit: true,
      authorize_del: true,
      tableLoading: false,

      item_per_page_list: [10, 20, 100, 200, 500, 1000],
      options: {
        page: 1,
        itemsPerPage: 20,
        sortBy: [],
        sortDesc: [],
      },
      // Pagination
      page: 1,
      pageCount: 0,
      itemsPerPage: 20,
      sortField: "checkindate",
      sortOrder: "DESC",
      reqSeq: 0,
      loadedOnce: false,
      totalItems: 0,
      dataAll2: [],
      mDataArray2: [],

      dialogAdd: false,
      text_color: "text-h5 green--text text-center",
      title: "green",
      message: "green",
      link: "",
      search_status: "",
      menu: false,
      menu2: false,
      search: "",
      toggleEnable: false,
      selectstatus: "",
      dataAll: [],
      status: ["Draft", "Request", "Approve", "Reject"],
      datefrom: null,
      dateto: null,
      dateRange: [],
      dateCurrent: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      seticonsearchmore: { icon: "mdi mdi-chevron-down", action: false },
      setlanguage: language.en,
      defaultlanguage: "en",
      // regexlang: "th",
      chooselanguage: [
        { name: "en", flag: "gb" },
        { name: "th", flag: "th" },
      ],
      aa: "สวัสดี",
      dialogEdit: false,
      dialogDelete: false,
      mDataArray: [],
      position: [],
      approver: [],
      department: [],
      department_list: [],
      position_list: [],
      company: [],
      authorize: [],
      // mDataArray: [
      //   {
      //     no: 1,
      //     date: "1/11/2022",
      //     name: "testname",
      //     customer: "ABS co Ltd",
      //     amount: "10,000",
      //     status: "Unapproved",
      //   },
      //   {
      //     no: 2,
      //     date: "2/11/2022",
      //     name: "testname2",
      //     customer: "ABS co Ltd",
      //     amount: "3,000",
      //     status: "Approved",
      //   },
      // ],
      headers: [
        { text: "ASSIGNMENT / CASE", value: "job_name", sortable: false },
        { text: "CLIENT", value: "customer_name", sortable: false },
        { text: "DATE", value: "checkindate_show", class: "text-no-wrap", cellClass: "text-no-wrap" },
        { text: "START - END TIME", value: "start_end", sortable: false, align: "center", class: "text-no-wrap", cellClass: "text-no-wrap" },
        { text: "HOURS", value: "durationShow", sortable: false, align: "center", class: "text-no-wrap", cellClass: "text-no-wrap" },
        { text: "CHARGE TYPE", value: "chargetype", sortable: false, class: "text-no-wrap", cellClass: "text-no-wrap" },
        { text: "DETAIL", value: "detail", sortable: false },
        { text: "ACTIONS", value: "actions", sortable: false, align: "center", width: "110px" },
      ],
      charge_list: [
        { value: "T/C", name: "Time Charge" },
        { value: "N/C", name: "No Charge" },
        { value: "F/C", name: "Fix Charge" },
        { value: "OFF", name: "ทำงานออฟฟิต" },
      ],
      jobTitle_list: [],
      searchCharge: null,
      searchChargeJob: null,
      searchEmp: [],
      delete_id: 0,
      dialogDeleteActivity: false,
    };
  },

  async beforeCreate() {
    console.log("beforeCreate");
    this.$store.state.navMenu = true;
  },
  async created() {
    // this.initialize();
  },
  components: {
    SuccessDialog,
  },
  beforeUpdate() { },
  computed: {
    dateRangeText() {
      if (!this.datefrom && !this.dateto) return "";
      return `${unity.formatDate(this.datefrom) || ""} - ${unity.formatDate(this.dateto) || ""}`;
    },
    showingText() {
      const total = Number(this.totalItems) || 0;
      if (!total) return "Showing 0 of 0 items";
      const start = (this.page - 1) * this.itemsPerPage + 1;
      const end = Math.min(this.page * this.itemsPerPage, total);
      return `Showing ${start}–${end} of ${total} items`;
    },
    computeddatefromFormatted() {
      return unity.formatDate(this.datefrom);
    },
    computeddateToFormatted() {
      return unity.formatDate(this.dateto);
    },
    itemsForSelected() {
      if (this.search.length) {
        return this.dataSource.filter((item) => this.search.includes(item));
      }
      return this.dataSource;
    },
  },
  async mounted() {
    await api.checkVersion();
    this.userId = localStorage.getItem(server.USER_ID);
    let yourUrlString = window.location;
    // alert(yourUrlString);

    let parser = document.createElement("a");
    parser.href = yourUrlString;

    this.authorize_id = localStorage.getItem(server.AUTHORIZE_ID);

    // alert("authorize_id:" + this.authorize_id);
    if (this.authorize_id == null || this.authorize_id == 0) {
      // this.$router.push("/login");
      this.$store.state.global_dialog = true;
      this.setupAlertDialog(
        true,
        "Authorize Failed",
        "Please Logout And Login Again",
        "text-h5 red--text text-center"
      );
      this.$router.back();
    }

    const router_path = parser.pathname.replace("/", "");

    const res_auth = await api.getAuthorize(this.userId, router_path);

    // console.log("res_auth:" + JSON.stringify(res_auth.data));

    this.authorize_view = res_auth.data[0].smd_view >= 1 ? true : false;
    this.authorize_add = res_auth.data[0].smd_add >= 1 ? true : false;
    this.authorize_edit = res_auth.data[0].smd_edit >= 1 ? true : false;
    this.authorize_del = res_auth.data[0].smd_del >= 1 ? true : false;

    // console.log("res_auth:" + JSON.stringify(res_auth.data));
    // console.log("authorize_view:" + this.authorize_view);
    // console.log("authorize_add:" + this.authorize_add);
    // console.log("authorize_edit:" + this.authorize_edit);
    // console.log("authorize_del:" + this.authorize_del);
    // this.$router.back();

    if (!this.authorize_view) {
      this.$router.push("/not-found");
    }

    // ----------------- Check Authorize ---------------------------

    let comp_id = localStorage.getItem(server.COMPANYID);
    // await this.loadCompanyMaster(comp_id);
    // await this.loadDepartmentMaster(comp_id);
    // await this.loadPositionMaster(comp_id);

    await this.loadAuthorize();
    await this.loaddata();

    this.userId = localStorage.getItem(server.USER_ID);
    if (this.userId && api.isLoggedIn()) {
      // this.$router.push("/");
    } else {
      this.$store.state.isLogged = false;
      this.$router.push("/login");
    }

    this.$hideLoader();
  },
  methods: {
    // ===== UI helpers (ไม่เปลี่ยน logic เดิม) =====
    onDateRangeChange(range) {
      const r = [...(range || [])].sort();
      this.datefrom = r[0] || null;
      this.dateto = r[1] || r[0] || null;
      this.menu = false;
      this.findAllByAPISearch();
    },
    async clearAll() {
      this.search = "";
      this.searchCharge = null;
      this.searchChargeJob = null;
      this.datefrom = null;
      this.dateto = null;
      this.dateRange = [];
      await this.findAllByAPISearch();
    },
    prevPage() {
      if (this.page > 1) this.loadDataByPage(this.page - 1);
    },
    nextPage() {
      if (this.page < this.pageCount) this.loadDataByPage(this.page + 1);
    },
    datePart(v) {
      if (!v) return "-";
      const m = String(v).match(/\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{4}/);
      return m ? m[0] : String(v).split(" ")[0];
    },
    timePart(v) {
      if (!v) return "--:--";
      const m = String(v).match(/\b([01]?\d|2[0-3]):[0-5]\d/);
      return m ? m[0].padStart(5, "0") : "--:--";
    },

    // 1. เพิ่มฟังก์ชัน findAllByAPISearch
    async findAllByAPISearch() {
      await this.onSearchPagination();
    },

    // ===== โหลดข้อมูลแบบแบ่งหน้า (ทุกฟังก์ชันใช้ sort เดียวกัน + กัน response เก่าทับ response ใหม่) =====
    async fetchPage(pageNumber) {
      const seq = ++this.reqSeq;
      this.tableLoading = true;
      try {
        const result = await api.getAllActivityByUserIDPaginationV2(
          pageNumber,
          this.itemsPerPage,
          this.sortField,
          this.sortOrder,
          localStorage.getItem(server.USER_ID),
          this.search,
          this.searchCharge,
          this.searchChargeJob,
          this.datefrom,
          this.dateto
        );
        if (seq !== this.reqSeq) return; // มี request ใหม่กว่าแล้ว ทิ้งผลนี้

        const tmp = (result.data.data || []).map((item) => {
          const totalMinutes = Math.round(Number(item.duration) || 0);
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          let durationText = "";
          if (hours > 0 && minutes > 0) {
            durationText = `${hours} ชั่วโมง ${minutes} นาที`;
          } else if (hours > 0) {
            durationText = `${hours} ชั่วโมง`;
          } else {
            durationText = `${minutes} นาที`;
          }
          return { ...item, durationShow: durationText };
        });

        this.dataAll2 = tmp;
        this.mDataArray2 = tmp;
        const pg = result.data.pagination || {};
        this.totalItems = Number(pg.totalRecords) || 0;
        this.page = Number(pg.currentPage) || pageNumber;
        this.itemsPerPage = Number(pg.recordsPerPage) || this.itemsPerPage;
        this.pageCount = Number(pg.totalPages) || 0;
      } catch (error) {
        if (seq === this.reqSeq) console.error("Error loading activity:", error);
      } finally {
        if (seq === this.reqSeq) this.tableLoading = false;
      }
    },

    // 3. loaddataPagination
    async loaddataPagination() {
      await this.fetchPage(1);
    },

    // 4. onOptionsUpdate (กดหัวตารางเพื่อ sort)
    async onOptionsUpdate(options) {
      const field = (options.sortBy && options.sortBy[0]) || "checkindate";
      const order = options.sortBy && options.sortBy[0]
        ? (options.sortDesc && options.sortDesc[0] ? "DESC" : "ASC")
        : "DESC";
      const changed = field !== this.sortField || order !== this.sortOrder;
      this.sortField = field;
      this.sortOrder = order;
      // โหลดครั้งแรก หรือเมื่อ sort เปลี่ยน → กลับไปหน้า 1
      if (changed || !this.loadedOnce) {
        this.loadedOnce = true;
        await this.fetchPage(1);
      }
    },

    // 5. loadDataByPage
    async loadDataByPage(pageNumber) {
      await this.fetchPage(pageNumber);
    },

    // 6. onSearchPagination
    async onSearchPagination() {
      this.page = 1;
      await this.fetchPage(1);
    },

    editItem() { },
    changelange: function (newVal, oldVal) { },
    showsearchmore() {
      this.toggleEnable = !this.toggleEnable;
      this.seticonsearchmore.action = !this.seticonsearchmore.action;
      if (this.seticonsearchmore.action === true) {
        this.seticonsearchmore.icon = "mdi mdi-chevron-up";
      } else {
        this.seticonsearchmore.icon = "mdi mdi-chevron-down";
      }
    },
    finddatafromdate() {
      this.searchStu_date = 1;
      this.mDataArray = this.dataAll.filter((item) => {
        if (this.searchCharge == null) {
          if (this.searchChargeJob == null) {
            if (this.datefrom != null && this.dateto != null) {
              if (
                item.checkInDate_S >= this.datefrom &&
                item.checkOutDate_S <= this.dateto
              ) {
                return item;
              }
            }
            if (this.datefrom == null && this.dateto == null) {
              return item;
            }
          } else {
            if (this.datefrom != null && this.dateto != null) {
              if (
                item.checkInDate_S >= this.datefrom &&
                item.checkOutDate_S <= this.dateto &&
                item.job_name == this.searchChargeJob
              ) {
                return item;
              }
            }
            if (this.datefrom == null && this.dateto == null) {
              if (item.job_name == this.searchChargeJob) {
                return item;
              }
            }
          }
        } else {
          if (this.searchChargeJob == null) {
            if (this.datefrom != null && this.dateto != null) {
              if (
                item.checkInDate_S >= this.datefrom &&
                item.checkOutDate_S <= this.dateto &&
                item.chargeType == this.searchCharge
              ) {
                return item;
              }
            }
            if (this.datefrom == null && this.dateto == null) {
              if (item.chargeType == this.searchCharge) {
                return item;
              }
            }
          } else {
            if (this.datefrom != null && this.dateto != null) {
              if (
                item.checkInDate_S >= this.datefrom &&
                item.checkOutDate_S <= this.dateto &&
                item.chargeType == this.searchCharge &&
                item.job_name == this.searchChargeJob
              ) {
                return item;
              }
            }
            if (this.datefrom == null && this.dateto == null) {
              if (
                item.chargeType == this.searchCharge &&
                item.job_name == this.searchChargeJob
              ) {
                return item;
              }
            }
          }
        }
      });
    },

    findChaege() {
      // เริ่มจากข้อมูลทั้งหมด
      let filteredData = [...this.dataAll2];

      // กรองด้วย search text
      if (this.search && this.search.trim() !== "") {
        const searchLower = this.search.toLowerCase();

        filteredData = filteredData.filter((item) => {
          return (
            (item.job_name || "").toLowerCase().includes(searchLower) ||
            (item.customer_name || "").toLowerCase().includes(searchLower) ||
            (item.checkInDate_show || "").toLowerCase().includes(searchLower) ||
            (item.checkOutDate_show || "")
              .toLowerCase()
              .includes(searchLower) ||
            (item.durationShow || "").toLowerCase().includes(searchLower) ||
            (item.chargeType || "").toLowerCase().includes(searchLower) ||
            (item.prospect || "").toLowerCase().includes(searchLower) ||
            (item.detail || "").toLowerCase().includes(searchLower)
          );
        });
      }

      // กรองด้วย Charge Type
      if (this.searchCharge) {
        filteredData = filteredData.filter(
          (item) => item.chargeType === this.searchCharge
        );
      }

      // กรองด้วย Assignment
      if (this.searchChargeJob) {
        filteredData = filteredData.filter(
          (item) => item.job_name === this.searchChargeJob
        );
      }

      // กรองด้วยช่วงวันที่
      if (this.datefrom && this.dateto) {
        filteredData = filteredData.filter(
          (item) =>
            item.checkInDate_S >= this.datefrom &&
            item.checkOutDate_S <= this.dateto
        );
      }

      // กรองด้วย Employee (ถ้ามี)
      if (this.searchEmp && this.searchEmp.length > 0) {
        filteredData = filteredData.filter((item) =>
          this.searchEmp.includes(Number(item.userId))
        );
      }

      // อัปเดตข้อมูลที่แสดงในตาราง
      this.mDataArray2 = filteredData;
    },
    async loadAuthorize() {
      const res_get = await api.getSettingGroupMenu();
      // console.log("res loadAuthorize : ", JSON.stringify(res_get));
      this.authorize = res_get.data;
    },

    async loaddata() {
      const res_joblist = await api.getAllJobtitleByDepartment(
        {
          params: {
            company_id: localStorage.getItem(server.COMPANYID),
            department: localStorage.getItem(server.DEPARTMENT_ID),
          },
        }
      );
      this.jobTitle_list = res_joblist.data;

      // const resData = await api.getAllActivityByUserID(
      //   localStorage.getItem(server.USER_ID)
      // );
      // this.dataAll = resData.data;
      // this.mDataArray = resData.data;
      // this.mDataArray.forEach((item) => {
      //   var decimalHour = Number(item.duration);
      //   const hours = Math.floor(decimalHour); // ได้ 3 ชั่วโมง
      //   const minutes = Math.round((decimalHour - hours) * 60);

      //   let durationText = "";
      //   if (hours > 0 && minutes > 0) {
      //     durationText = `${hours} ชั่วโมง ${minutes} นาที`;
      //   } else if (hours > 0) {
      //     durationText = `${hours} ชั่วโมง`;
      //   } else {
      //     durationText = `${minutes} นาที`;
      //   }
      //   item.durationShow = durationText;
      // });
      this.$hideLoader();
    },

    openeditItem(id) {
      this.$router.push(`/edit-activity/${id}`);
    },

    deleteItem(id) {
      this.delete_id = id;
      this.dialogDeleteActivity = true;
    },

    async deleteItemConfirm() {
      this.$showLoader();

      const delete_activity = await api.deleteActivity(this.delete_id);

      if (delete_activity.status == 200 || delete_activity.status == 201) {
        this.dialogDeleteActivity = false;
        await this.loadAuthorize();
        await this.loaddata();
        this.$hideLoader();
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Successfully  Deleted",
          "The activity has been successfully deleted.",
          "text-h5 green--text text-center"
        );
        return;
      } else {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "ลบข้อมูล",
          "ไม่สามารถลบได้",
          "text-h5 red--text text-center"
        );
      }
    },

    closeDelete() {
      this.delete_id = 0;
      this.dialogDeleteActivity = false;
    },
    setupAlertDialog(status, title, message, text_color) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
    },
  },
};
</script>

<style scoped>
.ma-page { background: #f4f6f9; }
.ma-card { border-radius: 12px !important; padding: 28px 32px; }
.ma-title { font-size: 30px; font-weight: 800; color: #111827; line-height: 1.2; }
.ma-subtitle { font-size: 14px; color: #6b7280; margin-top: 4px; }
.ma-btn-primary { background-color: #1f2d4d !important; color: #fff !important; text-transform: none !important; letter-spacing: 0 !important; font-weight: 600; border-radius: 6px; padding: 0 20px !important; }
.ma-btn-outline { border-color: #1f2d4d !important; color: #1f2d4d !important; text-transform: none !important; letter-spacing: 0 !important; font-weight: 600; border-radius: 6px; }

.ma-filter { background: #f5f7fa; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 14px; }
.ma-filter-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.ma-search { flex: 1 1 320px; }
.ma-date { flex: 0 1 220px; }
.ma-select { flex: 0 1 220px; }
.ma-input >>> .v-input__slot { background: #fff !important; min-height: 38px !important; border-radius: 6px; }
.ma-input >>> fieldset { border-color: #d1d5db; }
.ma-clear { margin-left: auto; padding-left: 16px; border-left: 1px solid #e5e7eb; align-self: stretch; display: flex; align-items: center; }
.ma-clear a { color: #2563eb; text-decoration: underline; font-size: 14px; }

.ma-muted-text { font-size: 13px; color: #6b7280; }
.ma-nav { border-color: #d1d5db !important; border-radius: 6px !important; }

.ma-table-wrap { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
.ma-table >>> thead th { background: #eef1f5 !important; color: #374151 !important; font-size: 12px !important; font-weight: 700 !important; letter-spacing: 0.3px; height: 44px !important; }
.ma-table >>> tbody td { font-size: 14px !important; color: #1f2937; height: 48px !important; }
.ma-table >>> tbody tr:hover { background: #f9fafb !important; }
.ma-table >>> .v-data-table__empty-wrapper:hover { background: transparent !important; }

.ma-chip { display: inline-block; background: #eef2ff; color: #3730a3; border-radius: 4px; padding: 2px 8px; font-size: 12px; font-weight: 600; }
.ma-detail { max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ma-act { border-radius: 6px !important; }
.ma-act--edit { background: #eef2ff; color: #1f2d4d !important; }
.ma-act--del { background: #fdecec; color: #b91c1c !important; }

.ma-empty { padding: 28px 0 32px; text-align: center; }
.ma-empty-icon { width: 52px; height: 52px; border-radius: 50%; background: #f3f4f6; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 12px; }
.ma-empty-title { font-size: 15px; font-weight: 700; color: #111827; }
.ma-empty-sub { font-size: 13px; color: #6b7280; margin-top: 2px; }
</style>